import * as enabledApi from '@shared/api/enabledPlayApi';
import { StopAllOutputsCommand } from '@shared/constants/CommandValues';
import { isLicensedProfile, isLicenseValidForProfile } from '@shared/constants/profile/ProfileUtils';
import { DEVICE_CURRENT_PROFILE } from '@shared/constants/StorageValues';
import IUserAccess from '@shared/models/components/IUserAccess';
import UserDeviceProfileModel from '@shared/models/deviceProfiles/UserDeviceProfileModel';
import UserDeviceModel from '@shared/models/devices/UserDeviceModel';
import InvalidResult from '@shared/models/result/InvalidResult';
import IResult from '@shared/models/result/IResult';
import SuccessResult from '@shared/models/result/SuccessResult';
import _ from 'lodash';
import { create } from 'zustand';
import { publish, subscribe } from '../helpers/EventCallback';

/**
 * Container managing the device based on routing of /deviceId.
 * In the panel window, CurrentDeviceContainer.id is the same as deviceContainer.outputDeviceId.
 */

interface CurrentDeviceState {
    // State
    appTrackingCurrent: string;
    appTrackingCurrentReal: string;
    appTrackPrevious: string;
    currentProfile: UserDeviceProfileModel | null;
    device: UserDeviceModel | undefined;
    deviceTypeId: string | undefined;
    errors: string[];
    id: string | undefined;
    installedAppNames: string[];
    isLoading: boolean;
    loadInstalledAppNamesHandler: (() => void) | null;
    phiNativeResult: string;
    phiNativeStatus: string;
    previousProfileId: string | null;
    tryCommandHandlers: ((command: string, profile: UserDeviceProfileModel) => boolean)[];
    userLicenseAccess: IUserAccess | undefined;

    // Actions
    addTryCommandHandler: (handler: (command: string, profile: UserDeviceProfileModel) => boolean) => void;
    clearAll: () => void;
    clearListeners: () => void;
    loadInstalledAppNames: () => void;
    resetCurrentProfile: () => Promise<IResult<UserDeviceModel>>;
    resetPhiResult: () => void;
    setAppTrackingCurrent: (v: string) => void;
    setAppTrackingCurrentReal: (v: string) => void;
    setAppTrackPrevious: (v: string) => void;
    updateAppTracking: (newAppName: string) => void;
    setInstalledAppNames: (names: string[]) => void;
    setIsLoading: (v: boolean) => void;
    setLoadInstalledAppNamesHandler: (handler: (() => void) | null) => void;
    setPhiNativeResult: (v: string) => void;
    setPhiNativeStatus: (v: string) => void;
    setPreviousProfileId: (id: string | null) => void;
    setUserLicenseAccess: (access: IUserAccess | undefined) => void;
    stopAllOutputs: () => Promise<boolean>;
    tryDeviceCommand: (command: string, additionalInputContent?: string) => Promise<boolean>;
    updateDeviceState: (device: UserDeviceModel | undefined, newProfile: UserDeviceProfileModel | null) => boolean;
    updatePhiResult: (token: string) => void;
}

export const useCurrentDeviceStore = create<CurrentDeviceState>()((set, get) => {
    const snapshotState = (profile: UserDeviceProfileModel | null) => {
        localStorage.setItem(DEVICE_CURRENT_PROFILE, JSON.stringify(profile));
    };

    const updateLocalState = (
        device: UserDeviceModel | undefined,
        newProfile: UserDeviceProfileModel | null
    ): boolean => {
        const existingProfileStr = JSON.stringify(get().currentProfile);
        const newProfileStr = JSON.stringify(newProfile);
        let updatedProfile = get().currentProfile;

        if (existingProfileStr !== newProfileStr) {
            if (newProfile && isLicenseValidForProfile(newProfile, get().userLicenseAccess)) {
                updatedProfile = newProfile;
            } else {
                updatedProfile = null;
            }
            snapshotState(updatedProfile);
        }

        set((state) => ({
            device,
            id: device?.id,
            deviceTypeId: device?.device?.deviceType.id,
            currentProfile: updatedProfile,
        }));

        publish('currentDeviceUpdated', { device, profile: updatedProfile });
        return true;
    };

    const setIsLoading = (v: boolean) => set({ isLoading: v });

    const setPreviousProfileId = (id: string | null) => set({ previousProfileId: id });

    const setInstalledAppNames = (names: string[]) => set({ installedAppNames: names });

    const setPhiNativeResult = (v: string) => set({ phiNativeResult: v });

    const setPhiNativeStatus = (v: string) => set({ phiNativeStatus: v });

    const updatePhiResult = (token: string) => {
        const newVal = get().phiNativeResult + token;
        set({ phiNativeResult: newVal });
    };

    const resetPhiResult = () => set({ phiNativeResult: '' });

    const setAppTrackingCurrent = (v: string) => set({ appTrackingCurrent: v });

    const setAppTrackingCurrentReal = (v: string) => set({ appTrackingCurrentReal: v });

    const setAppTrackPrevious = (v: string) => set({ appTrackPrevious: v });

    const updateAppTracking = (newAppName: string) =>
        set((state) => ({
            appTrackPrevious: state.appTrackingCurrentReal,
            appTrackingCurrentReal: newAppName,
            appTrackingCurrent: newAppName,
        }));

    const setUserLicenseAccess = (access: IUserAccess | undefined) => set({ userLicenseAccess: access });

    const addTryCommandHandler = (handler: (command: string, profile: UserDeviceProfileModel) => boolean) =>
        set((state) => ({ tryCommandHandlers: [...state.tryCommandHandlers, handler] }));

    const setLoadInstalledAppNamesHandler = (handler: (() => void) | null) => {
        set({ loadInstalledAppNamesHandler: handler });
    };

    const loadInstalledAppNames = () => {
        const handler = get().loadInstalledAppNamesHandler;
        if (handler) {
            handler();
        }
    };

    const updateDeviceState = (
        device: UserDeviceModel | undefined,
        newProfile: UserDeviceProfileModel | null
    ): boolean => {
        const currentState = get(); // Get current Zustand state (source of truth)
        let isDeviceEqual = false;
        if (device && currentState.device) {
            const isIdEqual = device.id === currentState.device.id;
            const isModifiedDateEqual = device.modifiedDate === currentState.device.modifiedDate;
            isDeviceEqual = isIdEqual && isModifiedDateEqual;
        }
        const isProfileEqual = _.isEqual(newProfile, currentState.currentProfile);
        if (isDeviceEqual && isProfileEqual) return false;
        return updateLocalState(device, newProfile);
    };

    const tryDeviceCommand = async (command: string, additionalInputContent?: string): Promise<boolean> => {
        const { tryCommandHandlers } = get();
        const currentProfile = get().currentProfile;
        if (!command || !currentProfile) return false; // If command or profile is not available, cannot execute command
        if (currentProfile && get().id && get().device) {
            if (!currentProfile) return false;
            for (const handler of tryCommandHandlers) {
                if (handler(command, currentProfile)) return true;
            }
        }
        return false;
    };

    const stopAllOutputs = async (): Promise<boolean> => {
        return await get().tryDeviceCommand(StopAllOutputsCommand);
    };

    const resetCurrentProfile = async (): Promise<IResult<UserDeviceModel>> => {
        const id = get().id;
        if (!id || !get().device) {
            return new InvalidResult<UserDeviceModel>('Device ID is not set');
        }
        const currentDeviceProfile = get().currentProfile;
        let result: IResult<UserDeviceModel>;
        if (currentDeviceProfile?.isPublic) {
            result = await enabledApi.updateDeviceProfile(id, { defaultProfileId: null });
        } else {
            result = await enabledApi.updateDeviceProfile(id, { customProfileId: null });
        }

        if (result.resultType === 'Ok' && result.data) {
            updateLocalState(result.data, null);
        } else if (result.resultType !== 'Ok') {
            const currentDevice = get().device;
            if (!currentDevice) {
                return new InvalidResult<UserDeviceModel>('Device not found');
            }
            const updatedDeviceState = { ...currentDevice };
            if (currentDeviceProfile?.isPublic) {
                updatedDeviceState.defaultProfileId = null;
            } else {
                updatedDeviceState.currentProfile = null;
            }
            updateLocalState(updatedDeviceState, null);
            result = new SuccessResult<UserDeviceModel>(updatedDeviceState);
        }
        return result;
    };

    const clearListeners = () => set({ tryCommandHandlers: [] });

    const clearAll = () => {
        set({
            device: undefined,
            currentProfile: null,
            previousProfileId: null,
            errors: [],
            id: undefined,
            deviceTypeId: undefined,
            isLoading: false,
            installedAppNames: [],
            tryCommandHandlers: [],
            loadInstalledAppNamesHandler: null,
            appTrackingCurrent: 'Cephable',
            appTrackingCurrentReal: 'Cephable',
            appTrackPrevious: 'Cephable',
            phiNativeResult: '',
            phiNativeStatus: '',
            userLicenseAccess: undefined,
        });
        localStorage.removeItem(DEVICE_CURRENT_PROFILE);
    };

    return {
        // State
        device: undefined,
        isLoading: false,
        previousProfileId: null,
        errors: [],
        id: undefined,
        deviceTypeId: undefined,
        currentProfile: null,
        appTrackingCurrent: 'Cephable',
        appTrackingCurrentReal: 'Cephable',
        appTrackPrevious: 'Cephable',
        installedAppNames: [],
        tryCommandHandlers: [],
        loadInstalledAppNamesHandler: null,
        phiNativeResult: '',
        phiNativeStatus: '',
        userLicenseAccess: undefined,

        // Actions
        addTryCommandHandler,
        clearAll,
        clearListeners,
        loadInstalledAppNames,
        resetCurrentProfile,
        resetPhiResult,
        setAppTrackingCurrent,
        setAppTrackingCurrentReal,
        setAppTrackPrevious,
        updateAppTracking,
        setInstalledAppNames,
        setIsLoading,
        setLoadInstalledAppNamesHandler,
        setPhiNativeResult,
        setPhiNativeStatus,
        setPreviousProfileId,
        setUserLicenseAccess,
        stopAllOutputs,
        tryDeviceCommand,
        updateDeviceState,
        updatePhiResult,
    };
});

// Module-level event subscriptions (replace useEffect([], []))
subscribe('userLicenseChangeLocal', async (data) => {
    if (data) {
        useCurrentDeviceStore.getState().setUserLicenseAccess(data);
        if (data.type === 'personal' && isLicensedProfile(useCurrentDeviceStore.getState().currentProfile)) {
            await useCurrentDeviceStore.getState().resetCurrentProfile();
        }
    }
});

subscribe('profileDeleted', async (data) => {
    if (data && data.profile?.id === useCurrentDeviceStore.getState().currentProfile?.id) {
        await useCurrentDeviceStore.getState().resetCurrentProfile();
    }
});
